import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getMyPayments, getMyPaymentsStats, type MyPaymentItem, type MyPaymentsStatsResponse } from '../../api/customerPayments'
import { usePaymentModalStore } from '../../store/paymentModalStore'
import { Receipt, TrendingUp } from 'lucide-react'

export default function MyPaymentsPage() {
  const { t } = useTranslation('portal')
  const openPaymentModal = usePaymentModalStore((s) => s.openModal)
  const [items, setItems] = useState<MyPaymentItem[]>([])
  const [total, setTotal] = useState(0)
  const [stats, setStats] = useState<MyPaymentsStatsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const [listRes, statsRes] = await Promise.all([
          getMyPayments(0, 50),
          getMyPaymentsStats(),
        ])
        if (!cancelled) {
          setItems(listRes.items)
          setTotal(listRes.total)
          setStats(statsRes)
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const formatDate = (iso: string) => {
    if (!iso) return '—'
    try {
      const d = new Date(iso)
      return d.toLocaleDateString(undefined, { dateStyle: 'medium' }) + ' ' + d.toLocaleTimeString(undefined, { timeStyle: 'short' })
    } catch {
      return iso
    }
  }

  if (loading) {
    return (
      <div className="py-8 text-center text-slate-500">
        {t('common.loading')}
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-8 text-center text-red-600">
        {error}
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('dashboard.previousPayments')}</h1>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center gap-2 text-slate-600 mb-1">
              <Receipt className="w-5 h-5" />
              <span className="text-sm font-medium">{t('dashboard.completedPayments')}</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats.completed_count}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center gap-2 text-slate-600 mb-1">
              <TrendingUp className="w-5 h-5" />
              <span className="text-sm font-medium">{t('dashboard.totalPaidBam')}</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{Number(stats.completed_amount_bam).toFixed(2)} BAM</p>
          </div>
        </div>
      )}

      <h2 className="text-lg font-semibold text-slate-800 mb-4">{t('dashboard.paymentHistory')}</h2>

      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-600">
          <p className="mb-4">{t('dashboard.noPaymentsYet')}</p>
          <button
            type="button"
            onClick={() => openPaymentModal()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('dashboard.payInvoice')}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-sm font-semibold text-slate-700">{t('dashboard.date')}</th>
                  <th className="px-4 py-3 text-sm font-semibold text-slate-700">{t('dashboard.receiptId')}</th>
                  <th className="px-4 py-3 text-sm font-semibold text-slate-700">{t('dashboard.amount')}</th>
                  <th className="px-4 py-3 text-sm font-semibold text-slate-700">{t('dashboard.status')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-700">{formatDate(p.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 font-mono">{p.receipt_id.slice(0, 8)}…</td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{p.amount} {p.currency}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        p.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > items.length && (
            <p className="px-4 py-2 text-sm text-slate-500 border-t border-slate-100">
              {items.length} / {total}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
